// @flow
import React, {type Node, useMemo, useEffect, useCallback} from 'react'
import {type RelayFragmentContainer, createFragmentContainer, graphql} from 'react-relay'

import idOrDatabaseId, {type UseAsValue} from 'utils/MiscUtils/idOrDatabaseId'
import Select, {type Option} from 'components/ui/forms/Select'
import {type FakeEvent} from 'utils/forms/fakeInputEvent'
import type {FormikProps} from 'formik'
import PersonAvatar from 'components/people/PersonAvatar'
import DefaultAvatar from 'components/ui/icons/DefaultAvatar'

import styles from './index.scss'
import type {People, Person} from './types'

export type {
  Option,
}

type Props = $ReadOnly<{
  formikProps?: FormikProps<any>,
  label: string,
  people: People,
  useAsValue: UseAsValue,
  nonePerson?: Option | null,
  eachRoleFiller?: Option | null,
  hideLabel?: boolean,
  eachCircleMember?: Option | null,
  value: string | null,
  containerClassName?: string,
  onChange: (FakeEvent, boolean | string | null) => void,
  onBlur?: (FakeEvent) => void | () => void,
  warning: string | null,
  clearable?: boolean,
  placeholder: string,
  name: string,
  'test-id'?: ?string,
  disabled?: boolean,
}>

const personToOption = (useAsValue: UseAsValue) => (person: Person) => ({
  value: idOrDatabaseId(useAsValue, person),
  label: person.name,
  isAgent: person.isAgent,
  avatarUrl: person.avatarUrl,
  agentOperatorName: person.agentOperatorName,
  person,
})

const applicationStartArgsKey = '__applicationStartArgs'

function FallbackOptionAvatar({option}: {|option: Option|}): Node {
  const optionData: any = option
  const agentAvatarSrc = window[applicationStartArgsKey]?.assetPaths?.aiAgentAvatar

  if (optionData.isAgent && agentAvatarSrc)
    return <img className={styles.personAvatarSmall} src={agentAvatarSrc} alt="" />

  if (optionData.avatarUrl)
    return <img className={styles.personAvatarSmall} src={optionData.avatarUrl} alt="" />

  return <DefaultAvatar className={styles.personAvatarSmall} />
}

function PersonOptionAvatar({option}: {|option: Option|}): Node {
  const optionData: any = option
  const person: ?Person = optionData.person

  return (
    <span className={styles.optionAvatar}>
      {person ? <PersonAvatar person={person} size="small" /> : <FallbackOptionAvatar option={option} />}
    </span>
  )
}

function PeopleSelector({
  formikProps,
  useAsValue,
  people,
  disabled = false,
  hideLabel = false,
  nonePerson = null,
  eachRoleFiller = null,
  eachCircleMember = null,
  containerClassName,
  'test-id': testId = null,
  onBlur = () => {},
  clearable = false,
  ...selectProps
}: Props): Node {
  const formatOptionLabel = useCallback((option) => (
    <span className={styles.optionLabel}>
      <PersonOptionAvatar
        option={option}
      />
      <span className={styles.optionText}>
        <span>{option.label}</span>
        {option.isAgent && option.agentOperatorName && (
          <span className={styles.operatorText}>
            {' • '}
            {I18n.t('role.agent_operator', {name: option.agentOperatorName})}
          </span>
        )}
      </span>
    </span>
  ), [])

  const formatGroupLabel = useCallback((group) => (
    <div className={styles.groupHeading}>{group.label}</div>
  ), [])

  const options = useMemo(() => {
    const gqlPeopleOptions = people.map(personToOption(useAsValue))
    const nonGQLOptions: Array<Option> = []

    if (nonePerson)
      nonGQLOptions.push(nonePerson)

    if (people.length > 1 && eachRoleFiller)
      nonGQLOptions.push(eachRoleFiller)

    if (people.length > 1 && eachCircleMember)
      nonGQLOptions.push(eachCircleMember)

    const humanOptions = gqlPeopleOptions.filter((o) => !o.isAgent)
    const agentOptions = gqlPeopleOptions.filter((o) => o.isAgent)

    if (agentOptions.length > 0) {
      const grouped = []
      if (nonGQLOptions.length > 0)
        grouped.push(...nonGQLOptions)
      grouped.push({label: I18n.t('shared.people'), options: humanOptions})
      grouped.push({label: I18n.t('shared.agents'), options: agentOptions})
      return grouped
    }

    return [...nonGQLOptions, ...gqlPeopleOptions]
  }, [people, useAsValue, eachRoleFiller, eachCircleMember, nonePerson])

  const flatOptions = useMemo(() => {
    const result = []
    options.forEach((opt) => {
      if (opt.options)
        result.push(...opt.options)
      else
        result.push(opt)
    })
    return result
  }, [options])

  const initialValue = flatOptions.length === 1 || nonePerson
    ? flatOptions[0]?.value
    : null

  const secondOptionValue = flatOptions.length > 1 ? flatOptions[1].value : undefined

  useEffect(() => {
    if (nonePerson
      && people.length === 1
      && formikProps
      && formikProps.values.personId === nonePerson.value
      && flatOptions.length > 1)
      formikProps.setFieldValue('personId', flatOptions[1].value)
    // Including 'formikProps' will cause infinite recursion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people.length, nonePerson, secondOptionValue])

  return (
    <Select
      hideLabel={hideLabel}
      initialValue={initialValue}
      disabled={disabled}
      options={options}
      containerClassName={containerClassName}
      formatOptionLabel={formatOptionLabel}
      formatGroupLabel={formatGroupLabel}
      clearable={clearable}
      onBlur={onBlur}
      {...{'test-id': testId}}
      {...selectProps}
    />
  )
}

export default (createFragmentContainer(PeopleSelector, {
  people: graphql`
    fragment PeopleSelector_people on Person @relay(plural: true) {
      id
      databaseId
      name
      isAgent
      avatarUrl
      agentOperatorName

      ...PersonAvatar_person
    }
  `,
}): RelayFragmentContainer<typeof PeopleSelector>)
